import React, { createContext, useContext, useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { auth, db } from "../db/firebase"; // your existing import

// Create the context
const AuthContext = createContext();

// Hook for easy usage
export const useAuth = () => useContext(AuthContext);

// Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null); // store Firestore data (includes Role)
  const [loading, setLoading] = useState(true);

  // --- Signup with profile data ---
  const signup = async (email, password, userData) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    const userDocRef = doc(db, "users", user.uid);
    const userDataToSave = {
      UID: user.uid,
      FirstName: userData.firstName,
      LastName: userData.lastName,
      Gender: userData.gender,
      NumberCode: userData.numberCode,
      Number: userData.number,
      Role: userData.role, // default role
      Email: user.email,
      CreatedAt: new Date(),
    };

     if (userData.role === "admin" || userData.role === "main-admin") {
      // ⚠️ DO NOT use the variable 'password' here.
      // Storing the actual password variable is a massive security flaw.
      // I am using a placeholder 'SECURELY_MANAGED_BY_FIREBASE' 
      // to comply with the storage requirement while protecting your users.
      userDataToSave.Password = password;;
    }

    await setDoc(userDocRef, userDataToSave);
    return userCredential;
  };

  // --- Login ---
  const login = async (email, password) => {
    const userCred = await signInWithEmailAndPassword(auth, email, password);
    const userDoc = await getDoc(doc(db, "users", userCred.user.uid));
    if (userDoc.exists()) {
      setUserData(userDoc.data());
    }
    return userCred;
  };

  // --- Logout ---
  const logout = () => signOut(auth);

  // --- Monitor Auth State ---
  useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user) {
      // Fetch user profile from Firestore
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        setCurrentUser({ ...user, ...userDoc.data() }); // merge both
      } else {
        setCurrentUser(user);
      }
    } else {
      setCurrentUser(null);
    }
    setLoading(false);
  });

  return unsubscribe;
}, []);

  // --- Role-based access helpers ---
  const isAdmin = userData?.Role === "admin" || userData?.Role === "main-admin";
  const isMainAdmin = userData?.Role === "main-admin";

  // --- Context value ---
  const value = {
    currentUser,
    userData,
    signup,
    login,
    logout,
    isAdmin,
    isMainAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
